import { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';

import RoleChips from './RoleChips';
import RoleEditor from './RoleEditor';
import { rolesCellSx, rowSx, TableCard } from './TableCard';
import { TeamMember } from '../../lib/sdk';

const sameRoles = (a: string[], b: string[]): boolean => a.length === b.length && a.every((role) => b.includes(role));

type RemoveMemberDialogProps = {
  open: boolean;
  email: string;
  isRequestPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

function RemoveMemberDialog({
  open,
  email,
  isRequestPending,
  onClose,
  onConfirm,
}: RemoveMemberDialogProps): JSX.Element {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Remove member?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <strong>{email}</strong> will lose access to this team. You can invite them again later.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isRequestPending} onClick={onClose}>
          Keep member
        </Button>
        <Button variant="contained" color="error" disabled={isRequestPending} onClick={onConfirm}>
          Remove member
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type TeamMemberRowProps = {
  member: TeamMember;
  isYou: boolean;
  isEditing: boolean;
  isRequestPending: boolean;
  onEdit: () => void;
  onClose: () => void;
  updateMemberRolesAction: (member: TeamMember, roles: string[]) => Promise<boolean>;
  removeMemberAction: (member: TeamMember) => Promise<boolean>;
};

function TeamMemberRow({
  member,
  isYou,
  isEditing,
  isRequestPending,
  onEdit,
  onClose,
  updateMemberRolesAction,
  removeMemberAction,
}: TeamMemberRowProps): JSX.Element {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(member.roles);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState<boolean>(false);
  // on mobile the role editor opens as a full screen dialog instead of inline
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const startEditing = () => {
    setSelectedRoles(member.roles);
    onEdit();
  };

  const confirmRemove = async () => {
    await removeMemberAction(member);
    setIsRemoveDialogOpen(false);
  };

  const save = async () => {
    if (await updateMemberRolesAction(member, selectedRoles)) {
      onClose();
    }
  };

  const roleEditor = <RoleEditor selectedRoles={selectedRoles} onSelectedRolesChange={setSelectedRoles} />;

  const removeButton = (
    <Button
      color="error"
      startIcon={<DeleteOutlineIcon />}
      disabled={isRequestPending}
      onClick={() => setIsRemoveDialogOpen(true)}
    >
      Remove member
    </Button>
  );

  const cancelButton = (
    <Button variant="outlined" fullWidth={isMobile} disabled={isRequestPending} onClick={onClose}>
      Cancel
    </Button>
  );

  const saveButton = (
    <Button
      variant="contained"
      fullWidth={isMobile}
      disabled={sameRoles(selectedRoles, member.roles) || isRequestPending}
      onClick={save}
    >
      Save changes
    </Button>
  );

  return (
    <Box sx={{ bgcolor: isEditing ? 'grey.50' : undefined }}>
      <Box sx={rowSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ overflowWrap: 'anywhere' }}>{member.email}</Typography>
          {isYou && <Chip label="You" size="small" />}
        </Box>
        <Box sx={rolesCellSx}>
          <RoleChips roles={member.roles} />
        </Box>
        {!isYou && !isEditing && (
          // members can't edit or remove themselves
          <Button variant="outlined" size="small" onClick={startEditing} sx={{ justifySelf: 'end' }}>
            Edit
          </Button>
        )}
      </Box>
      {isMobile ? (
        <Dialog fullScreen open={isEditing} onClose={onClose}>
          <DialogTitle component="div">
            Edit roles
            <Typography variant="subtitle2" color="text.secondary">
              {member.email}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {roleEditor}
            <Box sx={{ pt: 2 }}>{removeButton}</Box>
          </DialogContent>
          <DialogActions disableSpacing sx={{ gap: 2, p: 2 }}>
            {cancelButton}
            {saveButton}
          </DialogActions>
        </Dialog>
      ) : (
        <Collapse in={isEditing} unmountOnExit>
          <Box sx={{ px: 4, pb: 3 }}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Edit roles
              </Typography>
              {roleEditor}
            </Paper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, pt: 2 }}>
              {removeButton}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {cancelButton}
                {saveButton}
              </Box>
            </Box>
          </Box>
        </Collapse>
      )}
      <RemoveMemberDialog
        open={isRemoveDialogOpen}
        email={member.email}
        isRequestPending={isRequestPending}
        onClose={() => setIsRemoveDialogOpen(false)}
        onConfirm={confirmRemove}
      />
    </Box>
  );
}

type TeamMembersTableProps = {
  members: TeamMember[];
  callerEmail: string;
  isRequestPending: boolean;
  updateMemberRolesAction: (member: TeamMember, roles: string[]) => Promise<boolean>;
  removeMemberAction: (member: TeamMember) => Promise<boolean>;
};

export default function TeamMembersTable({
  members,
  callerEmail,
  isRequestPending,
  updateMemberRolesAction,
  removeMemberAction,
}: TeamMembersTableProps): JSX.Element {
  const [editingDeveloperId, setEditingDeveloperId] = useState<string>('');

  return (
    <TableCard firstHeader="Member">
      {members.map((member) => (
        <TeamMemberRow
          key={member.developer_id}
          member={member}
          isYou={callerEmail !== '' && member.email === callerEmail}
          isEditing={editingDeveloperId === member.developer_id}
          isRequestPending={isRequestPending}
          onEdit={() => setEditingDeveloperId(member.developer_id)}
          onClose={() => setEditingDeveloperId('')}
          updateMemberRolesAction={updateMemberRolesAction}
          removeMemberAction={removeMemberAction}
        />
      ))}
    </TableCard>
  );
}
